import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { App, StackSummary } from '../types';
import LoadingSpinner from '../components/LoadingSpinner';
import ErrorMessage from '../components/ErrorMessage';

const Dashboard: React.FC = () => {
  const [apps, setApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshedAt, setRefreshedAt] = useState<Date>(new Date());

  const loadApps = () => {
    setLoading(true);
    setError(null);
    api
      .get<App[]>('/apps')
      .then((data) => {
        setApps(data);
        setRefreshedAt(new Date());
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(loadApps, []);

  if (loading && apps.length === 0) return <LoadingSpinner />;
  if (error) return <ErrorMessage message={error} />;

  const totalAnomalies = apps.reduce((sum, a) => sum + (a.anomalyCount24h || 0), 0);
  const totalMonitored = apps.reduce((sum, a) => sum + (a.monitoredLogGroups || 0), 0);
  const totalLogGroups = apps.reduce((sum, a) => sum + (a.totalLogGroups || 0), 0);
  const coveragePct =
    totalLogGroups > 0 ? Math.round((totalMonitored / totalLogGroups) * 100) : 0;
  const coverageTone = totalLogGroups === 0
    ? 'neutral'
    : totalMonitored === totalLogGroups
      ? 'green'
      : 'amber';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1>Dashboard</h1>
          <p className="text-sm text-slate-500 mt-1">
            Overview of registered apps and their stacks. Counts cover the last 24 hours.
          </p>
        </div>
        <div className="flex items-center gap-3 text-xs text-slate-500">
          <span>Updated {refreshedAt.toLocaleTimeString()}</span>
          <button type="button" className="btn" onClick={loadApps} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SummaryTile label="Apps" value={apps.length} />
        <SummaryTile label="Anomalies (24h)" value={totalAnomalies} tone={totalAnomalies > 0 ? 'amber' : 'green'} />
        <SummaryTile label="Apps with anomalies" value={apps.filter((a) => a.anomalyCount24h > 0).length} />
        <SummaryTile
          label="Monitoring coverage"
          value={totalLogGroups > 0 ? `${coveragePct}%` : '—'}
          sublabel={totalLogGroups > 0 ? `${totalMonitored} / ${totalLogGroups}` : 'no log groups discovered'}
          tone={coverageTone}
        />
      </div>

      {apps.length === 0 ? (
        <div className="card-padded text-sm text-slate-500">
          No applications registered. Add apps in <code className="kbd">config/apps.yaml</code> and redeploy.
        </div>
      ) : (
        <div className="space-y-4">
          {apps.map((app) => (
            <AppCard key={app.appId} app={app} />
          ))}
        </div>
      )}
    </div>
  );
};

const SummaryTile: React.FC<{
  label: string;
  value: number | string;
  sublabel?: string;
  tone?: 'green' | 'amber' | 'red' | 'neutral';
}> = ({ label, value, sublabel, tone = 'neutral' }) => {
  const toneClasses = {
    green: 'text-emerald-700',
    amber: 'text-amber-700',
    red: 'text-red-700',
    neutral: 'text-slate-900',
  }[tone];
  return (
    <div className="card-padded">
      <div className="label">{label}</div>
      <div className={`text-3xl font-semibold ${toneClasses}`}>{value}</div>
      {sublabel && <div className="text-xs text-slate-500 mt-1">{sublabel}</div>}
    </div>
  );
};

const AppCard: React.FC<{ app: App }> = ({ app }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const tone = app.anomalyCount24h > 0 ? 'amber' : 'green';

  return (
    <div className="card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 p-5">
        <div className="flex items-center gap-4">
          <CountRing count={app.anomalyCount24h} tone={tone} />
          <div>
            <button
              type="button"
              onClick={() => navigate(`/apps/${app.appId}`)}
              className="text-base font-semibold text-slate-900 hover:text-brand-700 text-left"
            >
              {app.displayName}
            </button>
            <div className="text-xs text-slate-500 mt-0.5">
              <code className="kbd">{app.appId}</code>
              <span className="ml-2">{app.anomalyCount24h} anomaly{app.anomalyCount24h === 1 ? '' : 'ies'} in last 24h</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn"
            onClick={() => navigate(`/apps/${app.appId}`)}
          >
            View report
          </button>
          <button
            type="button"
            className="btn-ghost"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
          >
            <span>{open ? 'Hide stacks' : 'Show stacks'}</span>
            <svg
              className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`}
              viewBox="0 0 20 20" fill="currentColor" aria-hidden
            >
              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.085l3.71-3.855a.75.75 0 0 1 1.08 1.04l-4.24 4.4a.75.75 0 0 1-1.08 0l-4.24-4.4a.75.75 0 0 1 .02-1.06Z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
      {open && <StackBreakdown appId={app.appId} />}
    </div>
  );
};

const CountRing: React.FC<{ count: number; tone: 'green' | 'amber' | 'red' }> = ({ count, tone }) => {
  const ring = {
    green: 'ring-emerald-200 bg-emerald-50 text-emerald-700',
    amber: 'ring-amber-200 bg-amber-50 text-amber-700',
    red: 'ring-red-200 bg-red-50 text-red-700',
  }[tone];
  return (
    <div className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-full ring-4 font-semibold text-lg ${ring}`}>
      {count}
    </div>
  );
};

const StackBreakdown: React.FC<{ appId: string }> = ({ appId }) => {
  const [stacks, setStacks] = useState<StackSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<StackSummary[]>(`/apps/${appId}/stacks`)
      .then((data) => {
        if (!cancelled) setStacks(data);
      })
      .catch((err) => !cancelled && setError(err.message));
    return () => {
      cancelled = true;
    };
  }, [appId]);

  if (error) {
    return (
      <div className="border-t border-slate-200 p-5">
        <ErrorMessage message={error} />
      </div>
    );
  }
  if (stacks === null) {
    return (
      <div className="border-t border-slate-200">
        <LoadingSpinner label="Loading stacks…" />
      </div>
    );
  }
  if (stacks.length === 0) {
    return (
      <div className="border-t border-slate-200 p-5 text-sm text-slate-500">
        No stacks discovered. Check selectors / tagging in <code className="kbd">config/apps.yaml</code>.
      </div>
    );
  }
  return (
    <div className="border-t border-slate-200 overflow-x-auto">
      <table className="table-base">
        <thead>
          <tr>
            <th>Stack</th>
            <th>Account</th>
            <th>Region</th>
            <th className="text-right">Log groups</th>
            <th className="text-right">Monitored</th>
            <th className="text-right">CRIT</th>
            <th className="text-right">HIGH</th>
            <th className="text-right">MED</th>
            <th className="text-right">LOW</th>
            <th>Last seen</th>
          </tr>
        </thead>
        <tbody>
          {stacks.map((s) => (
            <tr key={`${s.stackId}-${s.accountId}-${s.region}`}>
              <td className="font-medium text-slate-900">
                <span className={s.stackId === 'unknown' ? 'italic text-slate-500' : ''}>{s.stackId}</span>
              </td>
              <td className="text-slate-600 font-mono text-xs">{s.accountId || '—'}</td>
              <td className="text-slate-600">{s.region || '—'}</td>
              <td className="text-right tabular-nums text-slate-600">{s.logGroupCount}</td>
              <MonitoredCell monitored={s.monitoredLogGroupCount ?? 0} total={s.logGroupCount} />
              <PrioCell value={s.byPriority.CRITICAL} variant="critical" />
              <PrioCell value={s.byPriority.HIGH} variant="high" />
              <PrioCell value={s.byPriority.MEDIUM} variant="medium" />
              <PrioCell value={s.byPriority.LOW} variant="low" />
              <td className="text-slate-500 text-xs whitespace-nowrap">
                {s.lastAnomalyAt ? formatRelative(s.lastAnomalyAt) : '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const MonitoredCell: React.FC<{ monitored: number; total: number }> = ({ monitored, total }) => {
  if (total === 0) {
    return <td className="text-right tabular-nums text-slate-300">—</td>;
  }
  if (total === 1) {
    const enabled = monitored === 1;
    return (
      <td className="text-right">
        <span className={enabled ? 'badge-ok' : 'badge-medium'}>{enabled ? 'Yes' : 'No'}</span>
      </td>
    );
  }
  const tone =
    monitored === 0
      ? 'text-slate-400'
      : monitored === total
        ? 'text-emerald-700 font-semibold'
        : 'text-amber-700 font-semibold';
  return <td className={`text-right tabular-nums ${tone}`}>{monitored} / {total}</td>;
};

const PrioCell: React.FC<{ value: number; variant: 'critical' | 'high' | 'medium' | 'low' }> = ({
  value,
  variant,
}) => {
  if (value === 0) {
    return <td className="text-right tabular-nums text-slate-300">0</td>;
  }
  const color = {
    critical: 'text-red-700 font-semibold',
    high: 'text-red-600 font-semibold',
    medium: 'text-amber-700 font-semibold',
    low: 'text-slate-700',
  }[variant];
  return <td className={`text-right tabular-nums ${color}`}>{value}</td>;
};

function formatRelative(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  return `${day}d ago`;
}

export default Dashboard;
